import React, { useMemo, useState } from "react";
import {
  FONT_IMPORT_URL,
  COUNTRY_CODES,
  DESIGNATIONS,
  BUSINESS_AREAS,
  ARRIVAL_TIMES,
  REFERENCE_PERSONS,
  now,
  EVENTS,
  INITIAL_BOOKINGS,
  getEventStartDateTime,
  isPastEvent,
  isUpcomingEvent,
  formatDateLong,
  getCancellationState,
  formatHoursRemaining,
  validateFullName,
  validateEmail,
  validateWebsiteName,
  validateDropdown,
  validateMobile,
} from "../Constants/EventsPageConst";
import "../Styles/EventsPageStyle.css";

/* ============================================================
   REUSABLE COMPONENTS
   ============================================================ */

function SectionHeading({ children }) {
  return <h2 className="Event-section-heading">{children}</h2>;
}

function PrimaryButton({ children, onClick, type = "button", fullWidth, disabled }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`Event-btn-primary${fullWidth ? " Event-btn-full" : ""}`}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, type = "button", fullWidth }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`Event-btn-secondary${fullWidth ? " Event-btn-full" : ""}`}
    >
      {children}
    </button>
  );
}

function StatusPill({ children }) {
  return <span className="Event-status-pill">{children}</span>;
}

function EmptyState({ title, message }) {
  return (
    <div className="Event-empty-state">
      <p className="Event-empty-title">{title}</p>
      <p className="Event-empty-message">{message}</p>
    </div>
  );
}

function Toggle({ active, onChange }) {
  const tabs = [
    { key: "upcoming", label: "Upcoming Events" },
    { key: "past", label: "Past Events" },
    { key: "history", label: "Event History" },
  ];
  return (
    <div className="Event-toggle-wrap">
      <div className="Event-toggle" role="tablist" aria-label="Events view">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={active === tab.key}
            className={`Event-toggle-tab${active === tab.key ? " Event-toggle-tab-active" : ""}`}
            onClick={() => onChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function getCardStatusLabel(evt, booking) {
  if (isPastEvent(evt)) {
    if (evt.attended) return "Attended";
    return "Past";
  }
  if (!booking) return null;
  switch (booking.status) {
    case "pending":
      return "Pending";
    case "accepted":
      return "Accepted";
    case "cancelled":
      return "Cancelled";
    default:
      return null;
  }
}

function EventCard({ evt, booking, onView, mode }) {
  const statusLabel = getCardStatusLabel(evt, booking);
  return (
    <article className="Event-card">
      <img className="Event-card-image" src={evt.image} alt={evt.title} />
      <div className="Event-card-body">
        <h3 className="Event-card-title">{evt.title}</h3>
        <p className="Event-card-meta">{formatDateLong(evt.dateObj)}</p>
        <p className="Event-card-meta">
          {evt.startTimeLabel} &ndash; {evt.endTimeLabel}
        </p>
        <p className="Event-card-meta">{evt.location}</p>
        {evt.attendees && <p className="Event-card-attendees">{evt.attendees}</p>}
        <p className="Event-card-description">{evt.description}</p>
        {statusLabel && (
          <div className="Event-card-status-row">
            <StatusPill>{statusLabel}</StatusPill>
          </div>
        )}
        <div className="Event-card-cta">
          <PrimaryButton onClick={() => onView(evt)} fullWidth>
            {mode === "upcoming" ? "View Event" : "View Details"}
          </PrimaryButton>
        </div>
      </div>
    </article>
  );
}

/* ============================================================
   EVENT DETAILS
   ============================================================ */
function EventDetails({ evt, booking, mode, onBack, onBookSlot, onOpenStatus }) {
  const past = isPastEvent(evt);
  return (
    <section className="Event-details">
      <button className="Event-back-link" onClick={onBack}>
        Back to Events
      </button>

      <img className="Event-details-image" src={evt.image} alt={evt.title} />

      <div className="Event-details-header">
        <h2 className="Event-details-title">{evt.title}</h2>
        <p className="Event-details-meta">{formatDateLong(evt.dateObj)}</p>
        <p className="Event-details-meta">
          {evt.startTimeLabel} &ndash; {evt.endTimeLabel}
        </p>
        <p className="Event-details-meta">{evt.location}</p>
        {evt.attendees && <p className="Event-details-attendees">{evt.attendees}</p>}
      </div>

      <div className="Event-details-about">
        <h3 className="Event-subheading">About the Event</h3>
        <p className="Event-body-text">{evt.about}</p>
      </div>

      {mode === "upcoming" && !past && (
        <div className="Event-details-cta">
          {booking ? (
            <SecondaryButton onClick={() => onOpenStatus(evt, booking)}>
              View Request Status
            </SecondaryButton>
          ) : (
            <PrimaryButton onClick={() => onBookSlot(evt)}>Book Your Slot</PrimaryButton>
          )}
        </div>
      )}

      {mode !== "upcoming" && (
        <div className="Event-details-note">
          <p className="Event-body-text Event-muted">
            {evt.attended
              ? "You attended this event."
              : "This event has already taken place. Booking is closed."}
          </p>
        </div>
      )}
    </section>
  );
}

/* ============================================================
   BOOK YOUR SLOT OVERLAY
   ============================================================ */
function BookSlotOverlay({ evt, onClose, onSubmit }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    websiteName: "",
    designation: "",
    countryCode: "+91",
    countryShort: "IN",
    mobileNumber: "",
    businessArea: "",
    arrivalTime: "",
    referencePerson: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function markTouched(field) {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }

  function validateAll() {
    const nextErrors = {
      fullName: validateFullName(form.fullName),
      email: validateEmail(form.email),
      websiteName: validateWebsiteName(form.websiteName),
      designation: validateDropdown(form.designation, "Please select a designation."),
      countryCode: validateDropdown(form.countryCode, "Please select a country code."),
      mobileNumber: validateMobile(form.mobileNumber),
      businessArea: validateDropdown(form.businessArea, "Please select a core business area."),
      arrivalTime: validateDropdown(form.arrivalTime, "Please select an arrival time."),
      referencePerson: validateDropdown(form.referencePerson, "Please select a reference person."),
    };
    setErrors(nextErrors);
    setTouched({
      fullName: true,
      email: true,
      websiteName: true,
      designation: true,
      countryCode: true,
      mobileNumber: true,
      businessArea: true,
      arrivalTime: true,
      referencePerson: true,
    });
    return Object.values(nextErrors).every((v) => !v);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validateAll()) return;
    onSubmit({
      eventId: evt.id,
      fullName: form.fullName.trim().replace(/\s+/g, " "),
      email: form.email.trim(),
      websiteName: form.websiteName.trim(),
      countryCode: form.countryCode,
      countryShortCode: form.countryShort,
      mobileNumber: form.mobileNumber,
      designation: form.designation,
      businessArea: form.businessArea,
      arrivalTime: form.arrivalTime,
      referencePerson: form.referencePerson,
      status: "pending",
      requestedAt: formatDateLong(now),
    });
  }

  function handleNameChange(value) {
    const filtered = value.replace(/[^A-Za-z\s]/g, "");
    update("fullName", filtered);
  }

  function handleMobileChange(value) {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
    update("mobileNumber", digitsOnly);
  }

  function fieldError(field) {
    return touched[field] ? errors[field] : "";
  }

  return (
    <div className="Event-overlay-backdrop" role="presentation">
      <div
        className="Event-overlay-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="book-slot-title"
      >
        <div className="Event-overlay-head">
          <h2 id="book-slot-title" className="Event-overlay-title">
            Book Your Slot
          </h2>
          <button className="Event-close-text" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>

        <div className="Event-overlay-event-summary">
          <p className="Event-summary-title">{evt.title}</p>
          <p className="Event-summary-line">{formatDateLong(evt.dateObj)}</p>
          <p className="Event-summary-line">
            {evt.startTimeLabel} &ndash; {evt.endTimeLabel}
          </p>
          <p className="Event-summary-line">{evt.location}</p>
        </div>

        <form className="Event-form" onSubmit={handleSubmit} noValidate>
          <div className="Event-form-field">
            <label className="Event-label" htmlFor="fullName">
              Full Name <span className="Event-required">*</span>
            </label>
            <input
              id="fullName"
              className="Event-input"
              type="text"
              value={form.fullName}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => markTouched("fullName")}
              placeholder="Enter Full Name"
            />
            {fieldError("fullName") && (
              <p className="Event-error-text">{fieldError("fullName")}</p>
            )}
          </div>

          <div className="Event-form-field">
            <label className="Event-label" htmlFor="email">
              Email Address <span className="Event-required">*</span>
            </label>
            <input
              id="email"
              className="Event-input"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              onBlur={() => markTouched("email")}
              placeholder="Enter Email Address"
            />
            {fieldError("email") && <p className="Event-error-text">{fieldError("email")}</p>}
          </div>

          <div className="Event-form-field">
            <label className="Event-label" htmlFor="websiteName">
              Website Name <span className="Event-required">*</span>
            </label>
            <input
              id="websiteName"
              className="Event-input"
              type="text"
              value={form.websiteName}
              onChange={(e) => update("websiteName", e.target.value)}
              onBlur={() => markTouched("websiteName")}
              placeholder="Enter Website / Company Name"
            />
            {fieldError("websiteName") && (
              <p className="Event-error-text">{fieldError("websiteName")}</p>
            )}
          </div>

          <div className="Event-form-field">
            <label className="Event-label" htmlFor="designation">
              Select Designation <span className="Event-required">*</span>
            </label>
            <select
              id="designation"
              className="Event-input Event-select"
              value={form.designation}
              onChange={(e) => update("designation", e.target.value)}
              onBlur={() => markTouched("designation")}
            >
              <option value="">Select Designation</option>
              {DESIGNATIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            {fieldError("designation") && (
              <p className="Event-error-text">{fieldError("designation")}</p>
            )}
          </div>

          <div className="Event-form-field">
            <span className="Event-label">
              Mobile Number <span className="Event-required">*</span>
            </span>
            <div className="Event-mobile-row">
              <select
                className="Event-input Event-select Event-country-select"
                aria-label="Country Code"
                value={form.countryCode}
                onChange={(e) => {
                  const match = COUNTRY_CODES.find(
                    (c) => `${c.short}-${c.code}` === e.target.value
                  );
                  update("countryCode", match ? match.code : "");
                  update("countryShort", match ? match.short : "");
                }}
                onBlur={() => markTouched("countryCode")}
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={`${c.short}-${c.code}`} value={`${c.short}-${c.code}`}>
                    {c.short} {c.code}
                  </option>
                ))}
              </select>
              <div className="Event-mobile-input-wrap">
                <input
                  className="Event-input"
                  type="text"
                  inputMode="numeric"
                  value={form.mobileNumber}
                  onChange={(e) => handleMobileChange(e.target.value)}
                  onBlur={() => markTouched("mobileNumber")}
                  placeholder="Enter Mobile Number"
                />
                <span className="Event-counter">{form.mobileNumber.length}/10</span>
              </div>
            </div>
            {fieldError("mobileNumber") && (
              <p className="Event-error-text">{fieldError("mobileNumber")}</p>
            )}
          </div>

          <div className="Event-form-field">
            <label className="Event-label" htmlFor="businessArea">
              Core Business Area <span className="Event-required">*</span>
            </label>
            <select
              id="businessArea"
              className="Event-input Event-select"
              value={form.businessArea}
              onChange={(e) => update("businessArea", e.target.value)}
              onBlur={() => markTouched("businessArea")}
            >
              <option value="">Select Core Business Area</option>
              {BUSINESS_AREAS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
            {fieldError("businessArea") && (
              <p className="Event-error-text">{fieldError("businessArea")}</p>
            )}
          </div>

          <div className="Event-form-field">
            <label className="Event-label" htmlFor="arrivalTime">
              Arrival Time <span className="Event-required">*</span>
            </label>
            <select
              id="arrivalTime"
              className="Event-input Event-select"
              value={form.arrivalTime}
              onChange={(e) => update("arrivalTime", e.target.value)}
              onBlur={() => markTouched("arrivalTime")}
            >
              <option value="">Select Arrival Time</option>
              {ARRIVAL_TIMES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {fieldError("arrivalTime") && (
              <p className="Event-error-text">{fieldError("arrivalTime")}</p>
            )}
          </div>

          <div className="Event-form-field">
            <label className="Event-label" htmlFor="referencePerson">
              Reference Person <span className="Event-required">*</span>
            </label>
            <select
              id="referencePerson"
              className="Event-input Event-select"
              value={form.referencePerson}
              onChange={(e) => update("referencePerson", e.target.value)}
              onBlur={() => markTouched("referencePerson")}
            >
              <option value="">Select Reference Person</option>
              {REFERENCE_PERSONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            {fieldError("referencePerson") && (
              <p className="Event-error-text">{fieldError("referencePerson")}</p>
            )}
          </div>

          <div className="Event-form-submit">
            <PrimaryButton type="submit" fullWidth>
              Send Request
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================================================
   REQUEST SENT OVERLAY
   ============================================================ */
function RequestSentOverlay({ evt, booking, onViewRequests, onBackToEvents }) {
  return (
    <div className="Event-overlay-backdrop" role="presentation">
      <div className="Event-overlay-panel Event-overlay-panel-narrow" role="dialog" aria-modal="true">
        <h2 className="Event-overlay-title">Request Sent</h2>
        <p className="Event-body-text">
          Your request to attend this event has been submitted successfully.
        </p>

        <div className="Event-result-block">
          <p className="Event-result-label">Event</p>
          <p className="Event-result-value">{evt.title}</p>

          <p className="Event-result-label">Website / Company</p>
          <p className="Event-result-value">{booking.websiteName}</p>

          <p className="Event-result-label">Date</p>
          <p className="Event-result-value">{formatDateLong(evt.dateObj)}</p>

          <p className="Event-result-label">Location</p>
          <p className="Event-result-value">{evt.location}</p>

          <p className="Event-result-label">Status</p>
          <p className="Event-result-value">
            <StatusPill>Pending</StatusPill>
          </p>
        </div>

        <div className="Event-overlay-actions">
          <PrimaryButton onClick={onViewRequests} fullWidth>
            View My Requests
          </PrimaryButton>
          <SecondaryButton onClick={onBackToEvents} fullWidth>
            Back to Events
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   REQUEST STATUS OVERLAY (includes cancellation entry points)
   ============================================================ */
function RequestStatusOverlay({
  evt,
  booking,
  onClose,
  onOpenFreeCancel,
  onOpenFeeCancel,
}) {
  const eventStart = getEventStartDateTime(evt);
  const { state, hoursUntilEvent } = getCancellationState(eventStart, now);
  const isCancelled = booking.status === "cancelled";

  let statusHeading = "Pending";
  let statusMessage = "Your request is pending confirmation from the organizer.";
  if (booking.status === "accepted") {
    statusHeading = "Accepted";
    statusMessage = "Your request has been accepted by the organizer.";
  } else if (isCancelled) {
    statusHeading = "Cancelled";
    statusMessage = "This request has been cancelled.";
  }

  return (
    <div className="Event-overlay-backdrop" role="presentation">
      <div className="Event-overlay-panel Event-overlay-panel-narrow" role="dialog" aria-modal="true">
        <div className="Event-overlay-head">
          <h2 className="Event-overlay-title">Request Status</h2>
          <button className="Event-close-text" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>

        <p className="Event-status-heading">{statusHeading}</p>
        <p className="Event-body-text">{statusMessage}</p>

        <div className="Event-result-block">
          <p className="Event-result-label">Event Details</p>
          <p className="Event-result-value">{evt.title}</p>
          <p className="Event-result-value">{formatDateLong(evt.dateObj)}</p>
          <p className="Event-result-value">{evt.location}</p>
        </div>

        {!isCancelled && (
          <div className="Event-cancellation-block">
            {state === "free" && (
              <>
                <p className="Event-status-heading">Cancellation Available</p>
                <p className="Event-body-text Event-muted">
                  You may cancel your request without a cancellation fee because more
                  than 48 hours remain before the event.
                </p>
                <SecondaryButton onClick={onOpenFreeCancel} fullWidth>
                  Cancel Request
                </SecondaryButton>
              </>
            )}

            {state === "fee" && (
              <>
                <p className="Event-status-heading">Cancellation Fee Applies</p>
                <p className="Event-body-text Event-muted">
                  The event is within 48 hours. A cancellation fee of &#8377;20,000
                  will apply.
                </p>
                <SecondaryButton onClick={onOpenFeeCancel} fullWidth>
                  Cancel Request
                </SecondaryButton>
              </>
            )}

            {state === "unavailable" && (
              <>
                <p className="Event-status-heading">Cancellation Not Available</p>
                <p className="Event-body-text Event-muted">
                  {hoursUntilEvent <= 0
                    ? "The event has already started or has passed. Cancellation is no longer permitted."
                    : "The event is within the final 24 hours. Cancellation is no longer permitted."}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   FREE CANCELLATION CONFIRM OVERLAY
   ============================================================ */
function FreeCancelConfirmOverlay({ onKeep, onConfirm }) {
  return (
    <div className="Event-overlay-backdrop" role="presentation">
      <div className="Event-overlay-panel Event-overlay-panel-narrow" role="dialog" aria-modal="true">
        <h2 className="Event-overlay-title">Cancel Request?</h2>
        <p className="Event-body-text">Are you sure you want to cancel your request?</p>
        <p className="Event-body-text Event-muted">
          No cancellation fee will apply because more than 48 hours remain before the
          event.
        </p>
        <div className="Event-overlay-actions">
          <SecondaryButton onClick={onKeep} fullWidth>
            Keep Request
          </SecondaryButton>
          <PrimaryButton onClick={onConfirm} fullWidth>
            Cancel Request
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PAID CANCELLATION OVERLAY (24–48 hours, ₹20,000 fee)
   ============================================================ */
function FeeCancelOverlay({ evt, hoursUntilEvent, onKeep, onConfirm }) {
  return (
    <div className="Event-overlay-backdrop" role="presentation">
      <div className="Event-overlay-panel Event-overlay-panel-narrow" role="dialog" aria-modal="true">
        <h2 className="Event-overlay-title">Cancellation Request</h2>

        <p className="Event-summary-title">{evt.title}</p>
        <p className="Event-summary-line">{formatDateLong(evt.dateObj)}</p>
        <p className="Event-summary-line">{evt.startTimeLabel}</p>

        <div className="Event-result-block">
          <p className="Event-result-label">Time Remaining</p>
          <p className="Event-result-value">{formatHoursRemaining(hoursUntilEvent)}</p>

          <p className="Event-result-label">Cancellation Fee</p>
          <p className="Event-result-value">&#8377;20,000</p>
        </div>

        <p className="Event-body-text Event-muted">
          Your cancellation is being made within 48 hours of the event. A cancellation
          fee of &#8377;20,000 will apply.
        </p>
        <p className="Event-body-text">Do you want to continue?</p>

        <div className="Event-overlay-actions">
          <SecondaryButton onClick={onKeep} fullWidth>
            Keep Request
          </SecondaryButton>
          <PrimaryButton onClick={onConfirm} fullWidth>
            Cancel and Pay &#8377;20,000
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   CANCELLATION RESULT OVERLAY (free or paid)
   ============================================================ */
function CancelResultOverlay({ paid, onClose }) {
  return (
    <div className="Event-overlay-backdrop" role="presentation">
      <div className="Event-overlay-panel Event-overlay-panel-narrow" role="dialog" aria-modal="true">
        <h2 className="Event-overlay-title">Request Cancelled</h2>
        <p className="Event-body-text">Your request has been cancelled.</p>
        {paid && (
          <div className="Event-result-block">
            <p className="Event-result-label">Cancellation Fee</p>
            <p className="Event-result-value">&#8377;20,000</p>
          </div>
        )}
        <div className="Event-overlay-actions">
          <PrimaryButton onClick={onClose} fullWidth>
            Back to Events
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function EventsPage() {
  const [view, setView] = useState("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [bookings, setBookings] = useState(INITIAL_BOOKINGS);

  const [showBookOverlay, setShowBookOverlay] = useState(false);
  const [showSentOverlay, setShowSentOverlay] = useState(false);
  const [showStatusOverlay, setShowStatusOverlay] = useState(false);
  const [showFreeCancelOverlay, setShowFreeCancelOverlay] = useState(false);
  const [showFeeCancelOverlay, setShowFeeCancelOverlay] = useState(false);
  const [showCancelResultOverlay, setShowCancelResultOverlay] = useState(false);
  const [lastCancelWasPaid, setLastCancelWasPaid] = useState(false);

  const upcomingEvents = useMemo(
    () => EVENTS.filter((e) => isUpcomingEvent(e)),
    []
  );
  const pastEvents = useMemo(() => EVENTS.filter((e) => isPastEvent(e)), []);
  const historyEvents = useMemo(
    () => EVENTS.filter((e) => isPastEvent(e) && e.attended === true),
    []
  );

  function applySearch(list) {
    if (!searchQuery.trim()) return list;
    const q = searchQuery.trim().toLowerCase();
    return list.filter(
      (e) =>
        e.title.toLowerCase().includes(q) || e.location.toLowerCase().includes(q)
    );
  }

  const visibleEvents =
    view === "upcoming"
      ? applySearch(upcomingEvents)
      : view === "past"
      ? applySearch(pastEvents)
      : applySearch(historyEvents);

  function handleChangeView(nextView) {
    setView(nextView);
    setSelectedEvent(null);
    setSearchQuery("");
  }

  function handleViewEvent(evt) {
    setSelectedEvent(evt);
  }

  function handleBookSlot(evt) {
    setSelectedEvent(evt);
    setShowBookOverlay(true);
  }

  function handleSubmitBooking(bookingData) {
    setBookings((prev) => ({ ...prev, [bookingData.eventId]: bookingData }));
    setShowBookOverlay(false);
    setShowSentOverlay(true);
  }

  function handleViewRequestsFromSent() {
    setShowSentOverlay(false);
    setShowStatusOverlay(true);
  }

  function handleBackToEventsFromSent() {
    setShowSentOverlay(false);
    setSelectedEvent(null);
  }

  function handleOpenStatus(evt, booking) {
    setSelectedEvent(evt);
    setShowStatusOverlay(true);
  }

  function handleOpenFreeCancel() {
    setShowStatusOverlay(false);
    setShowFreeCancelOverlay(true);
  }

  function handleOpenFeeCancel() {
    setShowStatusOverlay(false);
    setShowFeeCancelOverlay(true);
  }

  function handleConfirmFreeCancel() {
    setBookings((prev) => ({
      ...prev,
      [selectedEvent.id]: { ...prev[selectedEvent.id], status: "cancelled" },
    }));
    setShowFreeCancelOverlay(false);
    setLastCancelWasPaid(false);
    setShowCancelResultOverlay(true);
  }

  function handleConfirmFeeCancel() {
    setBookings((prev) => ({
      ...prev,
      [selectedEvent.id]: { ...prev[selectedEvent.id], status: "cancelled" },
    }));
    setShowFeeCancelOverlay(false);
    setLastCancelWasPaid(true);
    setShowCancelResultOverlay(true);
  }

  const activeBooking = selectedEvent ? bookings[selectedEvent.id] : null;

  return (
    <div className="Event-page">
      <div className="Event-container">
        <header className="Event-header">
          <h1 className="Event-heading-main">Events</h1>
          <p className="Event-heading-sub">
            Discover upcoming events, explore past events, and view the events you
            have attended.
          </p>
        </header>

        <Toggle active={view} onChange={handleChangeView} />

        {!selectedEvent && (
          <>
            <div className="Event-search-wrap">
              <input
                className="Event-search-input"
                type="text"
                placeholder="Search events by name or location"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search events"
              />
            </div>

            {visibleEvents.length === 0 && view === "upcoming" && (
              <EmptyState
                title="No Upcoming Events"
                message="There are currently no upcoming events available."
              />
            )}
            {visibleEvents.length === 0 && view === "past" && (
              <EmptyState
                title="No Past Events"
                message="There are no past events to display."
              />
            )}
            {visibleEvents.length === 0 && view === "history" && (
              <EmptyState
                title="No Event History"
                message="Events you attend will appear here."
              />
            )}

            {visibleEvents.length > 0 && (
              <div className="Event-grid">
                {visibleEvents.map((evt) => (
                  <EventCard
                    key={evt.id}
                    evt={evt}
                    booking={bookings[evt.id]}
                    mode={view}
                    onView={handleViewEvent}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {selectedEvent && (
          <EventDetails
            evt={selectedEvent}
            booking={bookings[selectedEvent.id]}
            mode={view}
            onBack={() => setSelectedEvent(null)}
            onBookSlot={handleBookSlot}
            onOpenStatus={handleOpenStatus}
          />
        )}
      </div>

      {showBookOverlay && selectedEvent && (
        <BookSlotOverlay
          evt={selectedEvent}
          onClose={() => setShowBookOverlay(false)}
          onSubmit={handleSubmitBooking}
        />
      )}

      {showSentOverlay && selectedEvent && activeBooking && (
        <RequestSentOverlay
          evt={selectedEvent}
          booking={activeBooking}
          onViewRequests={handleViewRequestsFromSent}
          onBackToEvents={handleBackToEventsFromSent}
        />
      )}

      {showStatusOverlay && selectedEvent && activeBooking && (
        <RequestStatusOverlay
          evt={selectedEvent}
          booking={activeBooking}
          onClose={() => setShowStatusOverlay(false)}
          onOpenFreeCancel={handleOpenFreeCancel}
          onOpenFeeCancel={handleOpenFeeCancel}
        />
      )}

      {showFreeCancelOverlay && (
        <FreeCancelConfirmOverlay
          onKeep={() => {
            setShowFreeCancelOverlay(false);
            setShowStatusOverlay(true);
          }}
          onConfirm={handleConfirmFreeCancel}
        />
      )}

      {showFeeCancelOverlay && selectedEvent && (
        <FeeCancelOverlay
          evt={selectedEvent}
          hoursUntilEvent={
            getCancellationState(getEventStartDateTime(selectedEvent), now)
              .hoursUntilEvent
          }
          onKeep={() => {
            setShowFeeCancelOverlay(false);
            setShowStatusOverlay(true);
          }}
          onConfirm={handleConfirmFeeCancel}
        />
      )}

      {showCancelResultOverlay && (
        <CancelResultOverlay
          paid={lastCancelWasPaid}
          onClose={() => {
            setShowCancelResultOverlay(false);
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
}
